<template>
  <PageTemplate>

    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          {{ $t('meeting_room.title') }}
        </h6>

        <CreateButton
            :label="$t('meeting_room.create')"
            :to="{ name: 'meetingRoomForm' }">
        </CreateButton>
      </div>

      <div class="element-box">
        <h5 class="form-header mb-4">
          {{ $t('meeting_room.list') }}
        </h5>

        <Search
            :filters="filters"
            :loading="isSearching"
            :officeDropdown="officeDropdown"
            @search="getMeetingRooms"
        />

        <PerPage :values="[10, 25, 50, 100]" @update:perPage="getMeetingRooms"/>

        <EmptyState
            v-if="!meetingRooms.data?.length"
            icon="fa-door-open"
            title="Keine Besprechungsräume gefunden"
            subtitle="Es wurden keine Besprechungsräume gefunden, die Ihren Suchkriterien entsprechen."/>

        <div v-else class="table-responsive overflow-auto pt-4">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>
                <span>Bezeichnung</span>
              </th>
              <th>
                <span>Standorte</span>
              </th>
              <th style=" text-align: right; padding-right: 30px; min-width: 150px;">
                <a href="#">{{ $t('general.actions') }}</a>
              </th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(meetingRoom) in meetingRooms.data" :key="meetingRoom.id">
              <td>
                {{ meetingRoom?.name }}
              </td>
              <td>
                {{ meetingRoom?.office?.name }}
              </td>
              <td class="text-right">
                <button class="btn btn-primary"
                        @click="router.push({name: 'meetingRoomForm', params: {id: meetingRoom?.id} })">
                  <i class="os-icon os-icon-edit"></i>
                </button>
                <button class="btn btn-danger ml-2"
                        @click="deleteModal(meetingRoom?.id)">
                  <i class="os-icon os-icon-trash"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <Pagination
            v-if="meetingRooms?.pagination?.total_pages > 1"
            :totalPages="meetingRooms?.pagination?.total_pages"
            :currentPage="meetingRooms?.pagination?.current_page"
            :totalItems="meetingRooms?.pagination?.total_items"
            :perPage="meetingRooms?.pagination?.items_per_page"
            @pagechanged="getMeetingRooms"/>
      </div>
    </div>

    <ModalDelete
        @update:modelValue="updateModal"
        :modelValue="showModal"
        :header="t('general.delete_item_header')"
        :onDelete="deleteMeetingRoom">
    </ModalDelete>

  </PageTemplate>
</template>


<script setup>
import meetingRoomService from '@/api/MeetingRoom';
import CreateButton from '@/components/CreateButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import Loader from '@/components/Loader.vue';
import ModalDelete from '@/components/modal/ModalDelete.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import { CloseToast, ErrorToast, SuccessToast } from '@/components/VueToast';
import router from '@/router';
import { getOfficeDropdown } from '@/shared/dropdowns';
import Search from '@/views/App/MeetingRoom/Search.vue';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t }          = useI18n();
const meetingRooms   = ref([]);
const filters        = ref({ officeId: null });
const showModal      = ref(false);
const meetingRoomId  = ref(null);
const isSearching    = ref(false);
const isLoaded       = ref(false);
const officeDropdown = ref([]);

async function getMeetingRooms(page = 1, perPage, passedFilters) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }

  const perPageRes = meetingRooms.value?.pagination?.items_per_page;

  try {
    const { data } = await meetingRoomService.getMeetingRooms({
      page: page,
      limit: perPage ?? perPageRes,
      ...(passedFilters ? passedFilters : filters.value),
    });

    meetingRooms.value = data;
    isSearching.value  = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

async function deleteMeetingRoom() {
  try {
    await meetingRoomService.deleteMeetingRoom(meetingRoomId.value);

    filters.value.officeId = null;
    showModal.value        = false;

    await getMeetingRooms();
    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}

function updateModal(value) {
  CloseToast();
  showModal.value = value;
}

function deleteModal(id) {
  CloseToast();
  meetingRoomId.value = id;
  showModal.value     = true;
}

onMounted(async () => {
  await Promise.all([
    getOfficeDropdown(officeDropdown),
    getMeetingRooms(),
  ]);

  isLoaded.value = true;
});

</script>

<style scoped>

</style>